/**
 * Reading one conversation, whichever platform it happened on.
 *
 * The inbox was built around two channels that behave the same way. An email
 * and a LinkedIn message both arrive as a record with a subject, a body and a
 * timestamp the sending system stamped, so a thread could be rendered without
 * ever asking where it came from. A device channel breaks all three of those.
 * There is no subject. The body is whatever a phone screen held. And the time
 * is often not a timestamp at all but a reading of what a chat app printed
 * above a bubble, which is the reason this module exists.
 *
 * Five rules run through it, and the first two are the same rule twice:
 *
 *   1. **An approximate time is never drawn as an exact one.** The ordinary
 *      timestamp puts the precise moment in a tooltip and in the `datetime`
 *      attribute, and on a reading taken off a screen both of those are a
 *      fabricated precision that reads as fact. So an approximate stamp is the
 *      day alone ("September 21, 2026", never a time), and its tooltip says it
 *      was read off the phone rather than stating a moment.
 *   2. **An attribution nobody could read is never drawn as a name.** A chat
 *      app prints a sender name once above a run of messages, so a thread read
 *      from part way down has bodies whose name has scrolled off. The engine
 *      says so, and files the message as theirs because that is the reading
 *      that does not lose a reply. Printing the person's name over that turns a
 *      safe guess into a fact, which is rule 1 again in a different column.
 *   3. **People are named.** A transcript is correspondence with a person, and
 *      a column of "They wrote" is the one thing that stops it reading as one.
 *      The name is whatever the record carries and never a guess.
 *   4. **The order on screen is the order it was said in.** A device channel
 *      has no per-message stamp to sort on, so this module sorts and does not
 *      trust the order rows arrive in.
 *   5. **A thread says what it cannot contain.** A platform whose history did
 *      not sync says so, because an operator closing a conversation off a short
 *      thread is acting on the absence of messages.
 *
 * It sits beside the page rather than inside it for the ordinary reason: there
 * is no DOM test environment in this workspace, so a pure module is the only
 * way any of this gets covered.
 */
import { channelReadsItsInbox } from '../../lib/channels'
import { relativeTimeFrom, toDate } from '../../lib/format'
import { humanizeCode } from '../../lib/labels'
import type { Channel, MessageDirection } from '../../lib/types'

// ---------------------------------------------------------------------------
// The page's own standing copy
// ---------------------------------------------------------------------------

/**
 * The description under the page title.
 *
 * It named email and LinkedIn until the device channels landed, which made the
 * two platforms it left out read as two platforms the inbox does not cover. It
 * names none of them now, so a platform arriving in the product does not leave
 * a sentence behind that is quietly wrong.
 */
export const INBOX_PAGE_DESCRIPTION =
  "Every reply, on every platform an account of yours sits on, in one place. A reply anywhere pauses that person's whole sequence, so nothing here is a race."

// ---------------------------------------------------------------------------
// Who said it
// ---------------------------------------------------------------------------

/** A non-empty name, or null. Blank strings arrive from both the wire and the phone. */
function named(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

export interface MessageAuthorLike {
  direction: MessageDirection
  /** The name recorded against this message, which on a device channel is what the phone read. */
  authorName?: string | null
  /**
   * Set when the engine could not tell who wrote this one.
   *
   * A chat app prints a sender name once above a run of messages rather than on
   * every bubble, so a thread read from part way down has bodies whose name is
   * off the top of the screen. The engine reports that rather than guessing,
   * and `direction` still arrives as `inbound` because filing an unread message
   * as ours is the reading that loses a reply. So this field is not a second
   * opinion about the direction, it is how much of one the direction is.
   *
   * Absent on every other source and on every row stored before the engine
   * could say, which is why it is read strictly and never for truthiness.
   */
  directionIsUnknown?: boolean
}

export interface ThreadPartiesLike {
  prospectName?: string | null
  /** The account this side of the conversation was held from. */
  channelAccountLabel?: string | null
}

/**
 * Drawn in place of a name when nobody read one.
 *
 * It describes the screen rather than the record, because that is the thing a
 * reader can do something about. "Unknown sender" would read as a person this
 * platform failed to identify, and this is not that: the person is known, the
 * thread is theirs, and the single message is the thing that was read without
 * a label above it.
 */
export const UNATTRIBUTED_AUTHOR_LABEL = 'Sender not shown'

/** The short marker beside it, for a reader scanning the column of names. */
export const UNATTRIBUTED_AUTHOR_BADGE = 'unconfirmed'

/**
 * Why the name is missing, said where somebody is reading the gap.
 *
 * Same reasoning as the approximate-time hint. "Sender not shown" on its own
 * reads as this platform losing something, and an operator seeing it above a
 * message that is plainly addressed to them will assume the pane is broken.
 * Saying what the phone saw turns it into what it is, which is a message read
 * below the only place its author was written.
 */
export const UNATTRIBUTED_AUTHOR_HINT =
  'A phone read this one off the screen with no sender name above it. Chat apps print a name once over a run of messages, so a message read part way down a thread carries none of its own. The side it sits on here is the safer reading rather than something anybody saw.'

export interface MessageAuthor {
  /**
   * `named` is a name to draw plainly. `unattributed` must not be drawn as one,
   * because every name this module could reach on such a row is a guess the
   * engine explicitly declined to make.
   */
  kind: 'named' | 'unattributed'
  /** The words to draw. */
  label: string
  /** Why the words are hedged. Empty when nothing is hedged. */
  hint: string
}

/**
 * Whose message this is, in as many words as the record actually supports.
 *
 * The message's own author wins, because on a device channel that is the name
 * the phone read off the screen and it is the most specific thing anybody
 * knows. The prospect's name is the fallback for an inbound message with no
 * author recorded, which is every email reply: the thread knows who it is with
 * even when the message does not say.
 *
 * Outbound is named for the account rather than for the platform. Several
 * accounts can hold a conversation on one platform, and "we sent" answers the
 * wrong question when somebody is working out which identity replied.
 *
 * The unknown-attribution flag beats both of those and beats the direction as
 * well, which is the part worth being deliberate about. The fallbacks above are
 * inferences from the thread, and they are good inferences precisely because
 * the direction was read. On a row where it was not, the same fallback prints a
 * real person's name over a message nobody could place. So the flag is checked
 * first, on either direction, and the flag is read strictly: an older API sends
 * none, and hedging every message in the product would be the worse lie.
 */
export function messageAuthorOf(
  message: MessageAuthorLike,
  thread: ThreadPartiesLike = {},
): MessageAuthor {
  if (message.directionIsUnknown === true) {
    return { kind: 'unattributed', label: UNATTRIBUTED_AUTHOR_LABEL, hint: UNATTRIBUTED_AUTHOR_HINT }
  }
  const label =
    message.direction === 'inbound'
      ? (named(message.authorName) ?? named(thread.prospectName) ?? 'They wrote')
      : (named(thread.channelAccountLabel) ?? 'We sent')
  return { kind: 'named', label, hint: '' }
}

/**
 * The label alone, for a caller that has no room to hedge.
 *
 * A thin read of `messageAuthorOf` rather than a second answer, so a screen
 * that only needs words still gets the honest ones.
 */
export function messageAuthorLabel(
  message: MessageAuthorLike,
  thread: ThreadPartiesLike = {},
): string {
  return messageAuthorOf(message, thread).label
}

// ---------------------------------------------------------------------------
// When it was said
// ---------------------------------------------------------------------------

/**
 * Why an approximate time is approximate, said where somebody is reading one.
 *
 * The reason matters more than the hedge. "About an hour ago" on its own reads
 * as sloppiness on this platform's part, and an operator seeing it beside an
 * exact time on the message underneath will assume one of the two is broken.
 * Saying where the reading came from turns it into what it is, which is the
 * best anybody can do with a time an app printed as a word.
 */
export const APPROXIMATE_TIME_HINT =
  'A phone read this message off the screen, where the exact time was not shown, so this is close rather than exact.'

/** Said when a message carries no usable time at all. */
export const NO_TIME_RECORDED = 'No time was recorded for this message.'

/**
 * The tooltip on a date a phone read off a screen, in the list and in a
 * message header, the owner's brief word for word. The date itself is drawn
 * alone, with no time of day (`messageStampOf`, `rowTimeOf`).
 */
export const APPROXIMATE_DATE_TITLE = 'Approximate, read off the phone'

export interface MessageTimeLike {
  sentAt?: string | null
  /** Set by the API when the stamp was read off a screen rather than sent by a system. */
  sentAtIsApproximate?: boolean
}

export interface MessageTime {
  /**
   * `exact` hands the stamp to the ordinary timestamp control, tooltip and all.
   * `approximate` and `unknown` must not reach it, because that control states a
   * precise moment in its `title` and its `datetime` whatever it is given.
   */
  kind: 'exact' | 'approximate' | 'unknown'
  /** The words to draw. Empty on `exact`, where the timestamp control draws its own. */
  text: string
  /** Why the words are hedged. Empty when nothing is hedged. */
  hint: string
}

/**
 * How to draw one message's time.
 *
 * The flag is read strictly rather than for truthiness, and that is the whole
 * safety of this: an older server sends no flag, and an absent flag has to mean
 * the ordinary exact stamp rather than hedging every message in the product.
 * Only an explicit `true` hedges.
 *
 * An unparseable or missing stamp is its own answer rather than an approximate
 * one. "Around an hour ago" computed from nothing would be the exact failure
 * this module was written to prevent, one step further along.
 */
export function messageTimeOf(
  message: MessageTimeLike,
  now: Date = new Date(),
): MessageTime {
  const date = toDate(message.sentAt)
  if (date === null) return { kind: 'unknown', text: NO_TIME_RECORDED, hint: '' }
  if (message.sentAtIsApproximate !== true) return { kind: 'exact', text: '', hint: '' }
  return {
    kind: 'approximate',
    text: `around ${relativeTimeFrom(date, now)}`,
    hint: APPROXIMATE_TIME_HINT,
  }
}

/**
 * The moment on a message header, as a date somebody can read back to a
 * colleague: "July 26, 2024 at 10:59 AM", the reference's header.
 *
 * The relative stamp is right for a list you are scanning and wrong for the one
 * message you have opened: "3 weeks ago" is the answer to "is this fresh" and
 * nobody can quote it in a forward. So the pane states the absolute moment on
 * the message it is showing, the way every mail client does. Pinned to en-US
 * like the row's date, so the header reads as the reference's whoever looks.
 *
 * THE MOMENT ONLY ON AN EXACT STAMP, and that is the whole care in this
 * function. A message a phone read off a chat screen has no exact moment in the
 * record, so printing "July 26, 2024 at 10:59 AM" over one would be this
 * module's own rule broken in the most convincing way available: a fabricated
 * precision, formatted like a fact, in the place a reader looks for one. Such a
 * stamp states the day it is close to and nothing finer, "September 21, 2026",
 * with no time of day, no machine-readable moment, and "Approximate, read off
 * the phone" in its tooltip. It said "about Sep 21, 2026" until round four,
 * when the owner asked for the date alone with the tooltip. The day with no
 * time is still the hedge: every exact stamp beside it carries its time.
 *
 * The kind is carried through rather than recomputed, so there is one answer to
 * "is this time real" on this screen and not two that agree until somebody
 * edits one of them.
 */
export interface MessageStamp {
  kind: MessageTime['kind']
  /** What the header draws. */
  text: string
  /** The tooltip. On a hedged kind, why it is hedged. */
  title: string
  /** The ISO moment for a `<time>` element. Only on an exact stamp. */
  dateTime?: string
}

/** Said in a header where a message has no usable time at all. The sentence is its tooltip. */
export const MESSAGE_NO_TIME = 'No time'

function zoned(timeZone: string | undefined): { timeZone?: string } {
  return timeZone === undefined ? {} : { timeZone }
}

export function messageStampOf(
  message: MessageTimeLike,
  now: Date = new Date(),
  timeZone?: string,
): MessageStamp {
  const when = messageTimeOf(message, now)
  const date = toDate(message.sentAt)
  if (when.kind === 'unknown' || date === null) {
    return { kind: 'unknown', text: MESSAGE_NO_TIME, title: NO_TIME_RECORDED }
  }
  const day = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', ...zoned(timeZone) }).format(
    date,
  )
  if (when.kind === 'approximate') return { kind: 'approximate', text: day, title: APPROXIMATE_DATE_TITLE }
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...zoned(timeZone) }).format(
    date,
  )
  const text = `${day} at ${time}`
  return { kind: 'exact', text, title: text, dateTime: date.toISOString() }
}

// ---------------------------------------------------------------------------
// What address it was said from
// ---------------------------------------------------------------------------

export interface ThreadAddressesLike {
  /** The far side's address: an email address on email, the handle elsewhere. */
  prospectAddress?: string | null
  /** The sending account's own address. Null on a channel whose handle is not an address. */
  accountAddress?: string | null
  /** The far side's name, which the To line falls back to. */
  prospectName?: string | null
  /** The sending account's display name, which the To line falls back to. */
  channelAccountLabel?: string | null
}

/**
 * The address to put under a name in the message header, picked by direction.
 *
 * The two addresses sit on the CONVERSATION rather than on every message,
 * because a thread is correspondence between two identities and repeating both
 * on every row would be the same two strings a hundred times. So the direction
 * is what selects: an inbound message was written by the far side, an outbound
 * one by the account this side was held from.
 *
 * Null rather than a placeholder when the platform has no address. A device
 * channel often has a display name and nothing else, and drawing an empty
 * `<>` under a name would read as an address this platform failed to load
 * rather than one that was never part of the record.
 *
 * A message the engine could not attribute gets no address at all. Its
 * direction is the safer filing rather than something anybody read, and the
 * address is chosen BY direction, so drawing one would launder that filing into
 * a stated fact about who sent it. Same rule as the name above it.
 */
export function messageAddressOf(
  message: MessageAuthorLike,
  thread: ThreadAddressesLike,
): string | null {
  if (message.directionIsUnknown === true) return null
  const address =
    message.direction === 'inbound' ? thread.prospectAddress : thread.accountAddress
  return named(address)
}

/**
 * The other side of the same pair, for the "To:" line under the sender.
 *
 * UNLIKE THE SENDER'S ADDRESS, THIS FALLS BACK TO A NAME. The browser pass read
 * "To: 100-85-72-45-5555-u12-linkedin" on a LinkedIn thread: the account's
 * `handle` is whatever the engine registered the phone under, its tailnet
 * address, port and Android profile, and the API was sending it as though it
 * were an address. The server now sends no account address on a channel whose
 * handle is not one. Dropping the To line there would leave a reader with no way
 * to tell which of the brand's accounts a message reached, so it says the
 * account's display name instead, the same fallback the composer's "from" line
 * uses, and reads "To: Conor O'Neill". The far side gets the same treatment with
 * the person's name. The sender's `<address>` does not fall back, because angle
 * brackets around a display name would dress a name up as an address.
 *
 * An unattributed message still gets nothing, for the reason given above.
 */
export function messageRecipientOf(
  message: MessageAuthorLike,
  thread: ThreadAddressesLike,
): string | null {
  if (message.directionIsUnknown === true) return null
  return message.direction === 'inbound'
    ? (named(thread.accountAddress) ?? named(thread.channelAccountLabel))
    : (named(thread.prospectAddress) ?? named(thread.prospectName))
}

/**
 * Whether the name over a message is the person whose record the thread is
 * matched to, and so the name that links to that record.
 *
 * The reference links a message's sender to their contact. Ours does that only
 * when the name drawn IS the matched person: an inbound message the engine could
 * attribute, on a thread with a record behind it, whose own author is either
 * absent (every email reply) or the same name the thread carries. A phone reads
 * the name off the screen, and a group chat or a thread swept off the wrong
 * side can put somebody else's name over a message. Linking that name to the
 * matched record would send a reader to the wrong person's page from a name
 * that is not theirs.
 */
export function messageNamesTheContact(
  message: MessageAuthorLike,
  thread: { prospectId?: string | null; prospectName?: string | null },
): boolean {
  if (message.directionIsUnknown === true || message.direction !== 'inbound') return false
  if (named(thread.prospectId) === null) return false
  const own = named(message.authorName)
  if (own === null) return true
  const theirs = named(thread.prospectName)
  return theirs !== null && own.toLowerCase() === theirs.toLowerCase()
}

/**
 * The line under a name in the inbox, which is a different sentence depending on
 * whether the platform knows the person at all.
 *
 * "No title or company on record" is the right words on a PROSPECT: there is a
 * record and two of its fields are empty, and an operator reading it knows to go
 * and fill them in. On a conversation a phone swept it is quietly wrong. There is
 * no record. The platform has exactly one thing about this person, the name the
 * app drew on the screen, and reporting that as two missing fields invites
 * somebody to look for a row that was never created.
 *
 * The useful fact in that case is the one the operator would act on: this person
 * is not on their list. Said plainly, because it is not an error and it is not a
 * gap to go and fill. Most people in a mirrored inbox are strangers by
 * definition, which is the whole reason the mirror is worth having.
 *
 * A prospect id is the test, not an empty name, because a matched prospect with
 * no title and no company is the first case and must keep its own words.
 */
export const NOT_A_PROSPECT_LINE = 'Not matched to anyone on your list'
export const NO_TITLE_OR_COMPANY_LINE = 'No title or company on record'

export function inboxSubtitle(thread: {
  prospectId?: string | null
  prospectTitle?: string | null
  prospectCompany?: string | null
  prospectInternal?: boolean
}): string {
  // A teammate marked as a test recipient. Said first, because it changes what
  // the thread is: a test coming back, not a prospect answering.
  if (thread.prospectInternal === true) return TEAM_TEST_LINE
  const parts = [thread.prospectTitle, thread.prospectCompany].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== '',
  )
  if (parts.length > 0) return parts.join(' at ')
  const id = (thread.prospectId ?? '').trim()
  return id === '' ? NOT_A_PROSPECT_LINE : NO_TITLE_OR_COMPANY_LINE
}

/** The subtitle for a teammate an admin marked as a test recipient. */
export const TEAM_TEST_LINE = 'Team test recipient. Messages here are real messages to a teammate.'

/**
 * How to draw the date on a row in the conversation LIST, by the same rule the
 * thread pane uses for a message.
 *
 * Through `messageTimeOf` rather than beside it, because two copies of "when is
 * a time a reading" drift, and the half that drifts is always the one nobody is
 * looking at. The list was that half: the pane had hedged an approximate stamp
 * since it was built and the list had never heard of one, so a swept thread read
 * as the freshest conversation in the inbox while the pane immediately beside it
 * said the time was only close.
 *
 * The row's time is the last message's time, so the mapping is a rename and
 * nothing more.
 */
export function threadTimeOf(
  thread: { lastMessageAt?: string | null; lastMessageAtIsApproximate?: boolean },
  now: Date = new Date(),
): MessageTime {
  return messageTimeOf(
    { sentAt: thread.lastMessageAt, sentAtIsApproximate: thread.lastMessageAtIsApproximate },
    now,
  )
}


/**
 * The thread's subject, or nothing at all.
 *
 * Null is not a gap here, it is most of the product. Only email and InMail have
 * a subject line, and every chat channel the engine reads has none by
 * construction, so a placeholder like "(no subject)" would print a complaint
 * about a field that does not exist on that platform on the majority of threads
 * in the inbox. The pane draws the line when there is one and closes the space
 * when there is not.
 *
 * The API hardcoded `subject: null` on every message until the server track
 * fixed it at its cause, so the conversation's own subject was the only real
 * one on the payload and nothing drew that either.
 */
export function threadSubjectOf(thread: { subject?: string | null }): string | null {
  return named(thread.subject)
}

/** A subject with its reply and forward prefixes off, folded for comparing. */
function subjectKey(subject: string): string {
  let key = subject.trim().toLowerCase().replace(/\s+/g, ' ')
  for (;;) {
    const next = key.replace(/^(?:re|fwd?|aw|sv)\s*:\s*/, '')
    if (next === key) return key
    key = next
  }
}

/**
 * The subject to print over one message, or null.
 *
 * Only when it says something the heading does not. The reference prints the
 * subject once, as the reader's heading, and the browser pass found ours
 * printing it again in bold over every message of an email thread, because the
 * server sends each message's subject whether or not it matches the thread's.
 * "Re: " and "Fwd: " in front of the same words are the same subject. A message
 * that genuinely changed the subject keeps its line, because that is news.
 */
export function messageSubjectLine(
  message: { subject?: string | null },
  thread: { subject?: string | null },
): string | null {
  const own = named(message.subject)
  if (own === null) return null
  const heading = named(thread.subject)
  if (heading !== null && subjectKey(own) === subjectKey(heading)) return null
  return own
}

/**
 * The large bold line at the top of the thread pane.
 *
 * The subject when the thread has one, and otherwise the person's name. The
 * design heads every thread with a big line, and on a chat channel there is no
 * subject to put there, so the first cut closed the space and left the name at
 * body size, and the pane read as having no heading. The name is the thing that
 * identifies a chat, so it takes the heading's size, and the smaller name line
 * under it is dropped rather than printing the same words twice.
 */
export interface ThreadHeading {
  text: string
  /** True when the heading is the person's name, so the name line below is not repeated. */
  nameIsHeading: boolean
}

export function threadHeadingOf(thread: {
  subject?: string | null
  prospectName?: string | null
}): ThreadHeading {
  const subject = threadSubjectOf(thread)
  if (subject !== null) return { text: subject, nameIsHeading: false }
  return { text: named(thread.prospectName) ?? UNNAMED_THREAD_HEADING, nameIsHeading: true }
}

/** Only reachable on a thread with neither a subject nor a name, which the API does not produce today. */
export const UNNAMED_THREAD_HEADING = 'Conversation'

// ---------------------------------------------------------------------------
// Reactions on a message
// ---------------------------------------------------------------------------

/**
 * Why the chip under a message names nobody, said where somebody reads it.
 *
 * The owner, 2026-10-12, on a reaction the inbox drew as a message from Basel:
 * "it's saying it's from me, but it's not. It's you." X draws a chip under the
 * bubble with the reactions and how many there are, and nothing about who left
 * them, so that is all this says too.
 */
export const REACTIONS_HINT =
  'Reactions on this message, as the phone last saw them. The app shows which ones and how many, not who left them.'

export interface MessageReactionsLine {
  /** The chip as the app draws it, "🔥 1". */
  label: string
  /** The same for a screen reader, which reads a bare emoji badly. */
  spoken: string
  hint: string
}

/** The chip under a message, or null when the phone saw no reactions on it. */
export function messageReactionsOf(message: { reactions?: { emoji: string[]; count: number } | null }): MessageReactionsLine | null {
  const reactions = message.reactions
  if (reactions === null || reactions === undefined || reactions.emoji.length === 0 || reactions.count < 1) return null
  const emoji = reactions.emoji.join('')
  return {
    label: `${emoji} ${String(reactions.count)}`,
    spoken: `${reactions.count === 1 ? '1 reaction' : `${String(reactions.count)} reactions`}: ${emoji}`,
    hint: REACTIONS_HINT,
  }
}

// ---------------------------------------------------------------------------
// What order it was said in
// ---------------------------------------------------------------------------

export interface MessageOrderLike {
  sentAt?: string | null
  /**
   * Where the engine saw this message sitting in the thread, 0 at the oldest.
   *
   * Absent on every source that has real timestamps, and on every row stored
   * before the engine started sending it.
   */
  ordinal?: number | null
}

/** A total, transitive number compare. Subtraction is neither when both sides are infinite. */
function ascending(a: number, b: number): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/**
 * The sort key, with an unreadable stamp sent to the end rather than the front.
 *
 * A message whose time cannot be read cannot be placed, and the two places to
 * put something unplaceable are opposite claims. At the top it says this is the
 * oldest thing in the conversation, which is the sentence an operator reads
 * first and the one they act on. At the bottom it says only that this is the
 * part nobody could order, which is true.
 */
function stampOf(message: MessageOrderLike): number {
  const date = toDate(message.sentAt)
  return date === null ? Number.POSITIVE_INFINITY : date.getTime()
}

/** Missing sorts last among rows that tie, so the rows that do carry one keep their run. */
function ordinalOf(message: MessageOrderLike): number {
  return typeof message.ordinal === 'number' && Number.isFinite(message.ordinal)
    ? message.ordinal
    : Number.POSITIVE_INFINITY
}

/**
 * One thread's messages, oldest first, sorted here rather than trusted.
 *
 * Two keys, and both are needed, which is the part worth writing down.
 *
 * The stamp is the primary key because it is the key every other consumer
 * sorts on. The store reads a thread ordered by `sent_at`, and so does the
 * thread-list preview, the export and anything anybody writes next. Those
 * cannot all be taught about a second key, so the engine now spreads a thread's
 * stamps into distinct ascending values as it reads down the screen, and that
 * single change fixes the order everywhere at once.
 *
 * The ordinal is the secondary key because a spread can still tie. Two threads
 * swept in the same second, a stamp the app did publish sitting beside ones it
 * did not, a future source that stamps a batch at once: the ordinal is the only
 * thing in the row that says which came first on the screen.
 *
 * IT DOES NOT RESCUE THE ROWS THAT ARE ALREADY FLAT, and an earlier version of
 * this note claimed it did. Measured on the live database on 2026-09-13: a
 * LinkedIn mirror had stored 30 messages with one stamp per thread, to the
 * millisecond, and all five in one thread read 02:21:29.393. Those rows carry no
 * ordinal either, because the engine counted it and never sent it, and they can
 * never gain one: ingestion is insert-if-absent on a deterministic external id,
 * so re-reading the same thread files nothing and updates nothing. The only
 * repair for them is to delete them and sweep again, which is what was done.
 * This key is for the rows written from here on.
 *
 * The last key is arrival order, so two rows that tie on both are left exactly
 * as the server sent them. `Array.prototype.sort` is specified as stable, but
 * only for a comparator that returns 0, and a comparator that subtracts two
 * infinities returns NaN instead, which is not 0 and is not anything. So the
 * index is compared explicitly rather than left to the engine underneath.
 *
 * Pure and copying. The caller hands in the array it got from the query cache,
 * and sorting that in place would reorder a cached object other screens read.
 */
export function orderedTranscript<T extends MessageOrderLike>(messages: readonly T[]): T[] {
  return messages
    .map((message, arrived) => ({
      message,
      arrived,
      at: stampOf(message),
      ordinal: ordinalOf(message),
    }))
    .sort(
      (a, b) =>
        ascending(a.at, b.at) || ascending(a.ordinal, b.ordinal) || ascending(a.arrived, b.arrived),
    )
    .map((entry) => entry.message)
}

// ---------------------------------------------------------------------------
// Which messages are open
// ---------------------------------------------------------------------------

/**
 * One line of the transcript as the reader draws it.
 *
 * `message`: a message, `open` when its whole header and body show, and
 * `latest` on the newest one. `fold`: a run of earlier messages drawn as one
 * row that says how many there are, which a click opens.
 */
export type TranscriptEntry<T> =
  | { kind: 'message'; message: T; open: boolean; latest: boolean }
  | { kind: 'fold'; ids: string[] }

/**
 * Earlier messages beyond this many are folded, keeping the first one and the
 * last `FOLD_KEEP_LAST` as lines of their own.
 */
export const FOLD_FROM = 5
export const FOLD_KEEP_LAST = 2

/**
 * The transcript as Salesforge reads it: every message in full, header and
 * words, and the reply card under the one being answered.
 *
 * EVERY MESSAGE OPENS IN FULL, from round four. The reader used to open the
 * latest and draw each earlier one as a single line, and the owner's brief
 * says plainly that Salesforge shows a thread's messages in full. A person can
 * still fold any earlier message to its line by clicking its header (`closed`
 * holds those), and a click on the line opens it again. The latest never folds.
 *
 * A LONG THREAD STILL FOLDS ITS MIDDLE. Forty messages in full would put the
 * reply card far below the window, the draft-nobody-saw problem this reader was
 * rebuilt to end. So past `FOLD_FROM` earlier messages, the first stays (it is
 * how the conversation started), the last two stay (they are what is being
 * answered), and the run between is one row with its count, the way a mail
 * client folds a long thread. Opening the fold shows every message.
 *
 * Sorted here through `orderedTranscript`, so the latest is the latest said and
 * not the last row the server happened to send.
 */
export function transcriptEntries<T extends MessageOrderLike & { id: string }>(
  messages: readonly T[],
  state: { closed: ReadonlySet<string>; unfolded: boolean },
): TranscriptEntry<T>[] {
  const ordered = orderedTranscript(messages)
  const latest = ordered[ordered.length - 1]
  if (latest === undefined) return []
  const earlier = ordered.slice(0, -1)
  const line = (message: T): TranscriptEntry<T> => ({
    kind: 'message',
    message,
    open: !state.closed.has(message.id),
    latest: false,
  })
  const last: TranscriptEntry<T> = { kind: 'message', message: latest, open: true, latest: true }
  if (state.unfolded || earlier.length <= FOLD_FROM) return [...earlier.map(line), last]
  const first = earlier[0] as T
  const folded = earlier.slice(1, earlier.length - FOLD_KEEP_LAST)
  const kept = earlier.slice(earlier.length - FOLD_KEEP_LAST)
  return [line(first), { kind: 'fold', ids: folded.map((message) => message.id) }, ...kept.map(line), last]
}

/**
 * The first words of a message for its folded line: one run of text with the
 * line breaks and repeated spaces taken out. The line truncates it to fit, so
 * this only has to be short enough not to carry a whole email around.
 */
/**
 * A message body as the thread draws it: no trailing spaces on a line, and never
 * more than one blank line in a row.
 *
 * Email bodies come out of mail clients' plain-text parts hard wrapped and full
 * of blank runs where the html had spacing, and one Linoto autoresponder drew
 * seven empty lines in a row. The owner read that as "formatting and spacing
 * issues that will likely show up in recipients' inboxes too" (2026-10-24). The
 * recipients got the html. This is only how the thread shows the stored text,
 * which is left as it was received.
 */
export function messageBodyText(body: string | null | undefined): string {
  return (body ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[ \t\u00a0]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function messageGist(body: string | null | undefined, max = 200): string {
  const text = (body ?? '').replace(/\s+/g, ' ').trim()
  return text.length <= max ? text : text.slice(0, max)
}

// ---------------------------------------------------------------------------
// Where it came from
// ---------------------------------------------------------------------------

/**
 * How a message reached the platform.
 *
 * Worth saying under every message, because it is what a reader weighs the
 * message by. A delivered email is the sending system's own record. A line
 * somebody typed into a work-queue card is a person's account of what they saw,
 * and a transcript a phone pushed is a reading of a screen. All three are
 * evidence and they are not the same strength of evidence.
 */
export const MESSAGE_SOURCE_COPY: Record<string, string> = {
  instantly: 'Delivered by the email provider',
  manual_card: 'Typed in from a work-queue card',
  manual_entry: 'Logged by hand',
  engine: 'Pushed in by the device engine',
  import: 'Came from an imported archive',
  /*
    A sequence step the scheduler filed itself, and it files it the moment it
    ASKS for the send, before any handset has performed it. The bare word
    "Generated" read as a sent message. The ledger, not this row, is the record
    of what a phone actually sent (`packages/campaigns/src/message-history.ts`
    leaves these rows out for that reason), so the line says what the row is.
  */
  generated: 'Filed by the sequence when it asked for the send',
}

/**
 * The source in words, or null when none was recorded.
 *
 * A source this build has no sentence for is humanised rather than dropped or
 * printed raw, so a new ingest path shows up as readable words on the day it
 * starts writing messages rather than on the day somebody adds it here.
 */
export function messageSourceLine(source: string | null | undefined): string | null {
  const code = named(source)
  if (code === null) return null
  return MESSAGE_SOURCE_COPY[code] ?? humanizeCode(code)
}

// ---------------------------------------------------------------------------
// What a thread cannot contain
// ---------------------------------------------------------------------------

/**
 * The caveat one platform's transcripts have to carry.
 *
 * The WhatsApp on the handset is a linked device. History from before the link
 * never synced to it, so no amount of scrolling reaches what was never there.
 * That matters here rather than only on the work-queue card, because the same
 * absence is what somebody reads in this pane before deciding a conversation
 * went nowhere.
 */
export const TRANSCRIPT_CAVEAT: Partial<Record<Channel, string>> = {
  whatsapp:
    'This WhatsApp runs as a linked device, so anything said before the phone was linked never reached it. A short thread here is not proof that nothing else was said.',
}

export function transcriptCaveat(channel: Channel): string | null {
  return TRANSCRIPT_CAVEAT[channel] ?? null
}

/**
 * What an empty thread means, which is not the same thing on every platform.
 *
 * On a platform a phone reads, an empty thread is usually a thread nobody has
 * fetched yet, and the fix is a sweep on the accounts page. Saying "no messages
 * have been recorded" there and stopping is true and useless. Everywhere else
 * the messages arrive on their own and there is nothing for the reader to do,
 * so the sentence stops where it does.
 */
export function emptyTranscriptCopy(channel: Channel): string {
  return channelReadsItsInbox(channel)
    ? 'No messages have been recorded on this thread yet. A phone fetches them when this account is swept, which is asked for on the channel accounts page.'
    : 'No messages have been recorded on this thread yet.'
}
